/**
 * @author Theodore Kruczek
 * @description Orbital Object ToolKit (ootk) is a collection of tools for working
 * with satellites and other orbital objects.
 * @license AGPL-3.0-or-later
 * @copyright (c) 2025 Kruczek Labs LLC
 *
 * Many of the classes are based off of the work of @david-rc-dayton and his
 * Pious Squid library (https://github.com/david-rc-dayton/pious_squid) which
 * is licensed under the MIT license.
 *
 * Orbital Object ToolKit is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * Orbital Object ToolKit is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * Orbital Object ToolKit. If not, see <http://www.gnu.org/licenses/>.
 */
import { AngularDistanceMethod } from '../enums/AngularDistanceMethod';
import { DEG2RAD, RAD2DEG, TAU } from '../utils/constants';
import { angularDistance } from '../utils/functions';
import { radecToPosition, radecToVelocity } from './ObservationUtils';
/**
 * Represents a geocentric right ascension and declination observation.
 *
 * In geocentric coordinates, observations are considered from the Earth's center. This approach simplifies calculations
 * for distant celestial objects, as it assumes a uniform observation point that ignores the observer's specific
 * location on Earth.
 */
export class RadecGeocentric {
    epoch;
    rightAscension;
    declination;
    range;
    rightAscensionRate;
    declinationRate;
    rangeRate;
    constructor(epoch, rightAscension, declination, range, rightAscensionRate, declinationRate, rangeRate) {
        this.epoch = epoch;
        this.rightAscension = rightAscension;
        this.declination = declination;
        this.range = range;
        this.rightAscensionRate = rightAscensionRate;
        this.declinationRate = declinationRate;
        this.rangeRate = rangeRate;
        // Nothing to do here.
    }
    /**
     * Creates a RadecGeocentric object from the given parameters in degrees.
     * @param epoch - The epoch in UTC.
     * @param rightAscensionDegrees - The right ascension in degrees.
     * @param declinationDegrees - The declination in degrees.
     * @param range - The range in kilometers (optional).
     * @param rightAscensionRateDegrees - The right ascension rate in degrees per second (optional).
     * @param declinationRateDegrees - The declination rate in degrees per second (optional).
     * @param rangeRate - The range rate in kilometers per second (optional).
     * @returns A new RadecGeocentric object.
     */
    static fromDegrees(epoch, rightAscensionDegrees, declinationDegrees, range, rightAscensionRateDegrees, declinationRateDegrees, rangeRate) {
        const rightAscensionRate = rightAscensionRateDegrees
            ? rightAscensionRateDegrees * DEG2RAD
            : null;
        const declinationRate = declinationRateDegrees ? declinationRateDegrees * DEG2RAD : null;
        return new RadecGeocentric(epoch, rightAscensionDegrees * DEG2RAD, declinationDegrees * DEG2RAD, range, rightAscensionRate, declinationRate, rangeRate);
    }
    /**
     * Creates a RadecGeocentric object from a state vector in J2000 coordinates.
     * @param state - The J2000 state vector.
     * @returns A new RadecGeocentric object.
     */
    static fromStateVector(state) {
        const rI = state.position.x;
        const rJ = state.position.y;
        const rK = state.position.z;
        const vI = state.velocity.x;
        const vJ = state.velocity.y;
        const vK = state.velocity.z;
        const rMag = state.position.magnitude();
        const declination = Math.asin(rK / rMag);
        const rIJMag = Math.sqrt(rI * rI + rJ * rJ);
        let rightAscension;
        if (rIJMag !== 0) {
            rightAscension = Math.atan2(rJ, rI);
        }
        else {
            rightAscension = Math.atan2(vJ, vI);
        }
        const rangeRate = state.position.dot(state.velocity) / rMag;
        const rightAscensionRate = (vI * rJ - vJ * rI) / (-(rJ * rJ) - rI * rI);
        const declinationRate = (vK - rangeRate * (rK / rMag)) / rIJMag;
        return new RadecGeocentric(state.epoch, rightAscension % TAU, declination, rMag, rightAscensionRate, declinationRate, rangeRate);
    }
    /**
     * Gets the right ascension in degrees.
     * @returns The right ascension in degrees.
     */
    get rightAscensionDegrees() {
        return this.rightAscension * RAD2DEG;
    }
    /**
     * Gets the declination in degrees.
     * @returns The declination in degrees.
     */
    get declinationDegrees() {
        return this.declination * RAD2DEG;
    }
    /**
     * Gets the right ascension rate in degrees per second.
     * @returns The right ascension rate in degrees per second, or null if it is not available.
     */
    get rightAscensionRateDegrees() {
        return this.rightAscensionRate ? this.rightAscensionRate * RAD2DEG : null;
    }
    /**
     * Gets the rate of change of declination in degrees per second.
     * @returns The rate of change of declination in degrees per second, or null if not available.
     */
    get declinationRateDegrees() {
        return this.declinationRate ? this.declinationRate * RAD2DEG : null;
    }
    /**
     * Calculates the position vector in geocentric coordinates.
     * @param range - The range in kilometers (optional). If not provided, it uses the default range or 1.0 kilometer.
     * @returns The position vector in geocentric coordinates.
     */
    position(range) {
        const r = range ?? this.range ?? 1.0;
        return radecToPosition(this.rightAscension, this.declination, r);
    }
    /**
     * Calculates the velocity vector of the celestial object.
     * @param range - The range of the celestial object in kilometers. If not provided, it uses the stored range value.
     * @param rangeRate - The range rate of the celestial object in kilometers per second.
     * If not provided, it uses the stored range rate value.
     * @returns The velocity vector of the celestial object in kilometers per second.
     * @throws Error if the right ascension rate or declination rate is missing.
     */
    velocity(range, rangeRate) {
        if (!this.rightAscensionRate || !this.declinationRate) {
            throw new Error('Velocity unsolvable, missing ra/dec rates.');
        }
        const r = range ?? this.range ?? 1.0;
        const rd = rangeRate ?? this.rangeRate ?? 0.0;
        return radecToVelocity(this.rightAscension, this.declination, r, this.rightAscensionRate, this.declinationRate, rd);
    }
    /**
     * Calculates the angular distance between two celestial coordinates (RA and Dec).
     * @param radec - The celestial coordinates to compare with.
     * @param method - The method to use for calculating the angular distance. Default is `AngularDistanceMethod.Cosine`.
     * @returns The angular distance between the two celestial coordinates in radians.
     */
    angle(radec, method = AngularDistanceMethod.Cosine) {
        return angularDistance(this.rightAscension, this.declination, radec.rightAscension, radec.declination, method);
    }
    /**
     * Calculates the angle in degrees between two RadecGeocentric objects.
     * @param radec - The RadecGeocentric object to calculate the angle with.
     * @param method - The method to use for calculating the angular distance. Default is AngularDistanceMethod.Cosine.
     * @returns The angle in degrees.
     */
    angleDegrees(radec, method = AngularDistanceMethod.Cosine) {
        return this.angle(radec, method) * RAD2DEG;
    }
}
